var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { OTPInput } from 'input-otp';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { defaultCountries, parseCountry, usePhoneInput } from 'react-international-phone';
import { authApi } from 'root/src/shared/util/authApi';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { getErrorMessage } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Select } from 'root/src/client/v2/common/components/ui/select';
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const phoneUtil = PhoneNumberUtil.getInstance();
const isPhoneNumberValid = (phoneNumber) => {
    if (!phoneNumber) {
        return false;
    }
    try {
        return phoneUtil.isValidNumber(phoneUtil.parse(phoneNumber));
    }
    catch (_a) {
        return false;
    }
};
const countryOptions = defaultCountries.map((country) => {
    const parsed = parseCountry(country);
    return { value: parsed.iso2, label: `${parsed.name} (+${parsed.dialCode})` };
});
const getDefaultCountry = () => {
    var _a;
    const locale = typeof navigator !== 'undefined' ? (navigator.language || 'en-US') : 'en-US';
    const country = (_a = locale.split('-')[1]) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    const isValidCountry = country && defaultCountries.some(c => parseCountry(c).iso2 === country);
    return (isValidCountry ? country : 'us');
};
// Redesign rewrite of the legacy `VerifyPhoneNumber` (legacy used MUI TextField + ButtonNew + the
// Redux-coupled section hooks). Same two-step flow (phone -> OTP) and the same pure auth API
// (`authApi.sendSMSVerification` / `verifySMSCode`); on a verified code it calls `onVerified` (which the
// caller wires to the SMS giveaway-entry path). Phone formatting via the headless
// `react-international-phone` hook, rendered with redesign primitives.
export const RedesignGiveawaySmsVerify = memo(({ onVerified, disabled, disabledMessage }) => {
    const [step, setStep] = useState('phone');
    const [otpCode, setOtpCode] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const { inputValue, handlePhoneValueChange, phone, country, setCountry } = usePhoneInput({
        defaultCountry: getDefaultCountry(),
        countries: defaultCountries,
    });
    const sendCode = useMutation({
        mutationKey: ['redesign-send-sms-code'],
        mutationFn: (phoneNumber) => __awaiter(void 0, void 0, void 0, function* () { return authApi.sendSMSVerification(phoneNumber); }),
    });
    const verifyAndEnter = useMutation({
        mutationKey: ['redesign-verify-sms-and-enter'],
        mutationFn: (code) => __awaiter(void 0, void 0, void 0, function* () {
            yield authApi.verifySMSCode(code);
            yield onVerified();
        }),
    });
    useEffect(() => {
        if (resendCooldown <= 0) {
            return undefined;
        }
        const timer = setInterval(() => setResendCooldown(prev => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);
    const phoneValid = isPhoneNumberValid(phone);
    const onSendCode = () => __awaiter(void 0, void 0, void 0, function* () {
        if (disabled) {
            toast.error(disabledMessage || 'This action is currently disabled.');
            return;
        }
        if (!phoneValid) {
            toast.error('Please enter a valid phone number.');
            return;
        }
        try {
            yield sendCode.mutateAsync(phone);
            toast.success('Verification code sent.');
            setStep('code');
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        }
        catch (error) {
            toast.error(getErrorMessage(error));
        }
    });
    const onResend = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield sendCode.mutateAsync(phone);
            toast.success('Code resent.');
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        }
        catch (error) {
            toast.error(getErrorMessage(error));
        }
    });
    const onVerify = () => __awaiter(void 0, void 0, void 0, function* () {
        if (disabled) {
            toast.error(disabledMessage || 'This action is currently disabled.');
            return;
        }
        try {
            yield verifyAndEnter.mutateAsync(otpCode);
        }
        catch (error) {
            toast.error(getErrorMessage(error));
        }
    });
    if (step === 'phone') {
        return (<div className="flex w-full flex-col gap-3">
				<div className="flex flex-col gap-2 sm:flex-row">
					<div className="sm:w-44">
						<Select value={country.iso2} onValueChange={value => setCountry(value)} options={countryOptions} placeholder="Country" searchable searchPlaceholder="Search country..."/>
					</div>
					<Input type="tel" autoComplete="tel" placeholder="Phone number" value={inputValue} onChange={handlePhoneValueChange} className="flex-1"/>
				</div>
				<Button type="button" variant="brand" loading={sendCode.isPending} disabled={!phoneValid} onClick={onSendCode} className="w-full rounded-xl text-sm font-semibold">
					Send code
				</Button>
			</div>);
    }
    return (<div className="flex w-full flex-col gap-4">
			<p className="text-center text-sm text-muted-foreground">
				Enter the code sent to {phoneValid ? phoneUtil.format(phoneUtil.parse(phone), 1) : phone}
			</p>
			<OTPInput maxLength={OTP_LENGTH} value={otpCode} onChange={setOtpCode} containerClassName="flex justify-center gap-2" render={({ slots }) => (<div className="flex justify-center gap-2">
						{slots.map((slot, index) => {
                var _a;
                return (<div 
                // eslint-disable-next-line react/no-array-index-key
                key={index} className={cn('flex h-12 w-10 items-center justify-center rounded-lg border border-solid text-xl font-semibold text-foreground transition-colors', slot.isActive ? 'border-neon-cyan bg-white/[0.06]' : 'border-white/[0.1] bg-white/[0.03]')}>
								{(_a = slot.char) !== null && _a !== void 0 ? _a : (slot.hasFakeCaret && <span className="h-6 w-px animate-pulse bg-neon-cyan"/>)}
							</div>);
            })}
					</div>)}/>
			<Button type="button" variant="brand" loading={verifyAndEnter.isPending} disabled={otpCode.length < OTP_LENGTH} onClick={onVerify} className="w-full rounded-xl text-sm font-semibold">
				Verify and enter
			</Button>
			<div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
				<button type="button" onClick={onResend} disabled={sendCode.isPending || resendCooldown > 0} className="font-medium transition-colors hover:text-foreground disabled:opacity-50">
					{resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
				</button>
				<button type="button" onClick={() => { setStep('phone'); setOtpCode(''); setResendCooldown(0); }} className="font-medium transition-colors hover:text-foreground">
					Change number
				</button>
			</div>
		</div>);
});
